import { useEffect } from 'react';

import { Box, styled } from '@mui/material';

import type { CourseModel, ParticipantExtendedModel } from 'Courses/data';
import type { UserDisplayModel } from 'User/userDisplayModel';
import { getUserDisplayName } from 'Utils/user';

import { useDesktopFullscreen } from '../../../../components/fullscreen-desktop-viewer/desktop-fullscreen-context';
import { InactiveUsersCard, LivePreviewCard } from '../../../../components/shared';

import type { DesktopModel, ParticipantModel } from 'Api';

type Props = {
  course: CourseModel;
  participantList?: ParticipantExtendedModel[];
  trainer: ParticipantModel;
  columns: number;
  isMinimized: boolean;
  participantsMachineControlActive?: boolean;
  inactiveUsersCount?: number;
};

const Grid = styled(Box)<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns}, 1fr);
  gap: 20px;
`;

export const ParticipantLivePreviewList = ({
  course,
  participantList,
  trainer,
  columns,
  isMinimized,
  participantsMachineControlActive = false,
  inactiveUsersCount = 0
}: Readonly<Props>) => {
  const safeParticipantList = Array.isArray(participantList) ? participantList : [];

  const myDesktopInstanceId = course.myDesktop?.instanceId;
  let currentUserId = course.isTrainer ? (course.trainer?.id ?? '') : '';

  if (!currentUserId && myDesktopInstanceId) {
    const myParticipant = safeParticipantList.find(
      p => p.desktop?.instanceId === myDesktopInstanceId
    );

    if (myParticipant) {
      currentUserId = myParticipant.id;
    }
  }

  const { initializeDesktops } = useDesktopFullscreen();

  useEffect(() => {
    initializeDesktops(course, currentUserId, safeParticipantList, trainer);
  }, [initializeDesktops, course, currentUserId, safeParticipantList, trainer]);

  const livePreviewCards = safeParticipantList.map(participant => (
    <LivePreviewCard
      key={participant.id}
      course={course}
      user={
        {
          id: participant.id,
          displayName: getUserDisplayName(participant.displayName),
          avatarColor: participant.avatarColor,
          avatar: participant.avatar
        } as UserDisplayModel
      }
      desktop={participant.desktop as DesktopModel}
      isParticipantDesktop
      isMinimized={isMinimized}
      participantsMachineControlActive={participantsMachineControlActive}
    />
  ));

  return (
    <Grid columns={columns}>
      <>
        {livePreviewCards}

        {/* note: always add trainer card */}
        <LivePreviewCard
          course={course}
          user={
            {
              id: trainer.id,
              displayName: getUserDisplayName(trainer.displayName),
              avatarColor: trainer.avatarColor,
              avatar: trainer.avatar
            } as UserDisplayModel
          }
          desktop={trainer.desktop as DesktopModel}
          isTrainerDesktop
          isMinimized={isMinimized}
        />

        {inactiveUsersCount > 0 && (
          <InactiveUsersCard courseId={course.id} inactiveUsersCount={inactiveUsersCount} />
        )}
      </>
    </Grid>
  );
};
